/**
 * The strings that describe how the work was made, and the legal lines that sit under them.
 *
 * Constitution Art. 8 and the root CLAUDE.md: these are quoted verbatim from REPORT_SPEC and are
 * the only place they may exist. `provenance.test.ts` parses REPORT_SPEC itself and fails if a
 * single character here drifts from the document the founder accepted — so the document stays the
 * source of truth rather than becoming a stale copy of the code.
 *
 * Footer composition is DQ-34, answered by the founder 2026-09-13: line one is the variant's
 * provenance sentence verbatim, line two is the product line.
 */

export type Variant =
  | "indication"
  | "quick_scan"
  | "quick_scan_shadow"
  | "broker"
  | "standard_audit"
  | "transformation_plan"
  | "deal_screen";

/** REPORT_SPEC "Variants and provenance lines (v2.0)" — the product name each variant prints. */
export const PRODUCT_NAME: Record<Variant, string> = {
  indication: "Instant Indication",
  quick_scan: "Quick Scan",
  quick_scan_shadow: "Quick Scan",
  broker: "Opportunity Report",
  standard_audit: "Standard Audit",
  transformation_plan: "Transformation Plan",
  deal_screen: "Deal Screen",
};

/** REPORT_SPEC — how many pages each variant has. */
export const PAGE_COUNT: Record<Variant, number> = {
  indication: 1,
  quick_scan: 6,
  quick_scan_shadow: 6,
  broker: 6,
  standard_audit: 7,
  transformation_plan: 7,
  deal_screen: 8,
};

/** REPORT_SPEC: draft watermark until `reviewed_by` is set; the deal screen is always INTERNAL. */
export const WATERMARK = { draft: "DRAFT", internal: "INTERNAL · not for distribution" } as const;

export interface FooterMeta {
  variant: Variant;
  business_alias: string;
  /** ISO date the report was produced. */
  date: string;
  engine_version: string;
  confidence_overall: 1 | 2 | 3;
  /** Broker variant only — the firm the report is forwarded under. */
  broker_firm?: string | null;
  /** Standard Audit and Transformation Plan only — the person who signed it, and when. */
  reviewed_by?: string | null;
  reviewed_on?: string | null;
  /** Transformation Plan only. */
  partner_operators?: string[];
}

/**
 * The provenance sentence for each variant, verbatim from REPORT_SPEC. Placeholders are filled;
 * nothing else is touched.
 */
export function provenanceLine(meta: FooterMeta): string {
  const { variant } = meta;
  if (variant === "indication") {
    return "Indication · prepared by our systems from what you told us and what's public · unconfirmed";
  }
  const quickScan = `Prepared overnight by our systems and checked automatically before delivery · ${meta.engine_version}`;
  if (variant === "quick_scan" || variant === "quick_scan_shadow") return quickScan;
  if (variant === "broker") {
    // Shadow mode adds `shadow_approved_by` to the sidecar and nothing to the page: REPORT_SPEC is
    // explicit that it is "not shown to the client as a signature".
    if (!meta.broker_firm) throw new Error("broker variant needs the broker firm it is forwarded under");
    return `${quickScan} · Prepared for ${meta.broker_firm}; forwarded under their name`;
  }
  if (variant === "deal_screen") return WATERMARK.internal;
  if (!meta.reviewed_by || !meta.reviewed_on) {
    throw new Error(`${variant} is signed by a person: reviewed_by and reviewed_on are required`);
  }
  const signed = `Reviewed against the business's own records and signed by ${meta.reviewed_by}, ${meta.reviewed_on}`;
  if (variant === "standard_audit") return signed;
  if (variant === "transformation_plan") {
    const operators = meta.partner_operators ?? [];
    if (operators.length === 0) throw new Error("transformation plan names its partner operators");
    return `${signed} · Partner operators: ${operators.join(", ")}`;
  }
  throw new Error(`no provenance line for variant ${variant}`);
}

/** DQ-34 line two. The brand stays where REPORT_SPEC's footer put it; DQ-34 fixes the product slot. */
export function productLine(meta: FooterMeta, formattedDate: string): string {
  return [
    "Operator Capital",
    PRODUCT_NAME[meta.variant],
    meta.business_alias,
    formattedDate,
    `engine ${meta.engine_version}`,
    `confidence ${meta.confidence_overall}/3`,
    "Not financial product advice",
  ].join(" · ");
}

/** The two-line footer, in order (DQ-34). Printed on every page. */
export function footer(meta: FooterMeta, formattedDate: string): [string, string] {
  return [provenanceLine(meta), productLine(meta, formattedDate)];
}

/**
 * WEBSITE_SPEC §"The four marks" — what each confidence mark means, in the words the site uses.
 * The report, the indication and the console all explain it identically or the system is not one.
 */
export const CONFIDENCE_MARKS = {
  inferred: "worked out from what's public — treat it as a range",
  stated: "you or your broker told us; used as given",
  verified: "we saw it in the accounts, the IM or your Xero export",
  indication: "the whole free document: prepared by our systems from what you told us, unconfirmed",
} as const;

/** QA_GATES_AND_RELEASE §1 — what the client hears when a gate holds a report. */
export function holdNotice(byTime: string): string {
  return `One figure needs a second look before we send it — you'll have it by ${byTime}.`;
}

/** PRD §1 refund promise, restated for the client. DQ-38 set the trigger at the base case. */
export const REFUND_LINE =
  "If the base case comes in under A$10,000 a year, the Quick Scan is refunded in full.";

/**
 * The one approved claim about what happens to a Stage-1 document before it goes out (GA-69,
 * DQ-68, founder 2026-09-13). The words around it differ by surface — the footer says "Prepared
 * overnight by our systems and …", the website says "The indication and the Quick Scan are
 * prepared by our systems and …" — but this clause is the claim, and it is identical everywhere.
 * "Checked line by line" is retired: in autonomous mode no person reads the report, and the same
 * QA_GATES paragraph already forbids implying one does.
 */
export const STAGE1_CHECKED = "checked automatically before delivery";

/** Wordings that used to describe the same thing and must not come back (GA-69). */
export const RETIRED_CHECK_CLAIMS = [
  "checked line by line",
  "checked automatically before they reach you",
] as const;

/** REPORT_SPEC footer, INDICATION_SPEC §4. The sentence that must appear on everything. */
export const NOT_ADVICE = "Not financial product advice";

/** INDICATION_SPEC §4 — what the indication says when the row is too thin to price. */
export const INSUFFICIENT_INPUT =
  "There isn't enough here yet to put a defensible range on. A short conversation would fill the gaps.";

/**
 * DQ-75 — what the indication says when we can describe the business but cannot yet value it,
 * because we have no wage benchmark for its sector (GA-70).
 *
 * This is a different thing from a thin row and says so: the hours below are real, and the reason
 * there is no money range is ours, not theirs.
 */
export const NOT_PRICEABLE_YET =
  "We can see where the hours go, but we don't yet have the figures for this kind of business to put "
  + "a range on what they're worth. We're working on it, and we'll come back to you when we do.";
